import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { Links } from '../constants';
import { CheckoutOffer } from './offer';

// @stripe/stripe-js doesn't export ApplePayOption from its public entrypoint, so derive it
// from the PaymentElement options type.
type ApplePayOption = NonNullable<StripePaymentElementOptions['applePay']>;

/**
 * The months of membership today's charge covers, which the sheet shows as included before the renewal
 * starts. The checkout screen and `buildPriceBreakdown` state the same term; if it ever changes, they
 * move together.
 */
export const INCLUDED_MEMBERSHIP_MONTHS = 6;

/**
 * The apple pay recurringPaymentRequest for the membership renewal. Passing this to the
 * PaymentElement makes stripe request an apple pay merchant token (MPAN) rather than a single-use
 * device token (DPAN); the MPAN is what lets recurly charge the wallet card off-session when the
 * membership renews, so without it renewals can fail. It also makes the wallet sheet disclose the
 * renewal terms.
 *
 * The included months go in `trialBilling` at $0 ahead of the renewal, so the sheet reads "first 6
 * months included" and then "$189 every 12 months" rather than a bare "$189" beside a $20 total, which
 * read as though the membership were charged today. The shop's sheet does the same with its intro term.
 *
 * No dates: the first renewal is not exactly six months out (Recurly's first period has run about 197
 * days for nano purchases), and a date computed on the device would state one we do not charge on. The
 * durations are true whatever the exact day.
 *
 * Returns undefined when web reports no structured renewal terms, so apple pay falls back to a
 * standard (device token) request rather than disclosing terms we cannot verify.
 */
export function buildApplePayRecurringOption(offer: CheckoutOffer): ApplePayOption | undefined {
  const { renewalPriceInCents, renewalMonths } = offer;
  if (!renewalPriceInCents || renewalPriceInCents <= 0 || !renewalMonths || renewalMonths <= 0) {
    return undefined;
  }

  return {
    recurringPaymentRequest: {
      paymentDescription: `Fi Membership: first ${INCLUDED_MEMBERSHIP_MONTHS} months included`,
      managementURL: `${window.location.origin}${Links.manageHome}`,
      trialBilling: {
        label: `First ${INCLUDED_MEMBERSHIP_MONTHS} months included`,
        amount: 0,
        recurringPaymentIntervalUnit: 'month',
        recurringPaymentIntervalCount: INCLUDED_MEMBERSHIP_MONTHS,
      },
      regularBilling: {
        label: 'Fi Membership',
        amount: renewalPriceInCents,
        recurringPaymentIntervalUnit: 'month',
        recurringPaymentIntervalCount: renewalMonths,
      },
    },
  };
}
